import { useCallback, useEffect, useRef } from "react";
import type { PreVettingDraft } from "@chinese-vendor/schemas";

/**
 * Returns a debounced save function that fires `saveDraft` after `delay` ms.
 * Pending timers are cleared automatically on unmount so stale saves never fire
 * after the screen is torn down.
 */
export function useDebouncedDraftSave(
  saveDraft: (draft: PreVettingDraft) => Promise<void>,
  delay = 500,
): (draft: PreVettingDraft) => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback(
    (draft: PreVettingDraft) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        saveDraft(draft).catch(() => {});
      }, delay);
    },
    [saveDraft, delay],
  );
}
